import {
  type ApiResponse,
  apiClient,
  handleApiError,
  handleApiResponse,
} from './api'

// Types
export interface Organization {
  id: string
  name: string
  domain?: string
  hasCdpConnection?: boolean
  createdAt: string
  updatedAt: string
  memberCount?: number
  chatConfigurationsCount?: number
}

export interface OrganizationWithRole extends Organization {
  role?: {
    id: string
    name: string
    description?: string
    systemRole: boolean
  }
  isCurrent?: boolean
  isMember?: boolean
  canSwitch?: boolean
  canDelete?: boolean
}

export interface CreateOrganizationRequest {
  name: string
  domain?: string
}

export interface UpdateOrganizationRequest {
  name?: string
  domain?: string
}

export interface OrganizationsResponse {
  organizations: OrganizationWithRole[]
}

// Service class
export class OrganizationsService {
  /**
   * Get all organizations (Super Admin users see all, regular users see their own)
   */
  async getOrganizations(): Promise<OrganizationsResponse> {
    try {
      const response =
        await apiClient.get<ApiResponse<OrganizationsResponse>>(
          '/organizations'
        )
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Get a specific organization by ID
   */
  async getOrganization(id: string): Promise<OrganizationWithRole> {
    try {
      const response = await apiClient.get<
        ApiResponse<{ organization: OrganizationWithRole }>
      >(`/organizations/${id}`)
      return handleApiResponse(response).organization
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Create a new organization for the authenticated user
   */
  async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<OrganizationWithRole> {
    try {
      const response = await apiClient.post<
        ApiResponse<{ organization: OrganizationWithRole }>
      >('/organizations', {
        organization: data,
      })
      return handleApiResponse(response).organization
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Update an organization (God Mode only)
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<OrganizationWithRole> {
    try {
      const response = await apiClient.patch<
        ApiResponse<{ organization: OrganizationWithRole }>
      >(`/organizations/${id}`, {
        organization: data,
      })
      return handleApiResponse(response).organization
    } catch (error) {
      throw handleApiError(error)
    }
  }

  /**
   * Delete an organization (global admins or target-workspace Organization Admins)
   */
  async deleteOrganization(id: string): Promise<void> {
    try {
      await apiClient.delete(`/organizations/${id}`)
    } catch (error) {
      throw handleApiError(error)
    }
  }
}

// Export singleton instance
export const organizationsService = new OrganizationsService()
